/**
 * InpaintMaskEditor — 局部重绘遮罩编辑器（MJ + GPT 共用）
 *
 * 功能：
 *   - 在原图上绘制半透明红色遮罩（标记要重新生成的区域）
 *   - 支持画笔 / 橡皮擦切换、笔头大小调节
 *   - 可选提示词输入（引导重绘内容）
 *   - 提交时导出 mask 为 base64 PNG
 *
 * ── maskFormat prop（重要）──
 * 不同的上游 API 对 mask 的格式要求不同：
 *
 *   maskFormat='mj'（默认）:
 *     白色(255,255,255, alpha=255) = 重绘区域
 *     黑色(0,0,0, alpha=255) = 保留区域
 *     → 用于 MJ Vary Region
 *
 *   maskFormat='openai':
 *     透明(alpha=0) = 重绘区域
 *     不透明白色(255,255,255, alpha=255) = 保留区域
 *     → 用于 OpenAI GPT-Image-2 /v1/images/edits
 *     → 缩放到原图尺寸后会再次二值化清理半透明边缘像素
 *
 * ── exportMaskBase64 导出踩坑记录 ──
 *   Canvas 2D drawImage 使用 source-over 合成模式：
 *   源图的透明像素无法覆盖目标的不透明像素。
 *   因此 OpenAI 模式在缩放前必须用 clearRect（而非 fillRect 白底），
 *   否则所有透明区域（=重绘区域）会被白色背景吞掉，
 *   导出的 mask 100% 不透明 → API 认为整图保留 → mask 不生效。
 *
 * ── 其他 Props ──
 *   allowEmptyMask  — 是否允许不画遮罩就提交（GPT 模式用，整图重绘）
 *   hint            — 底部提示文字（覆盖默认的 MJ 提示语）
 *
 * Portal 渲染到 document.body，全屏覆盖
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Check, Brush, Eraser, Undo2, Redo2, Loader2
} from 'lucide-react';
import { API_BASE_URL } from './constants';
import { getToken } from './api';

// ── 样式常量 ──
// 预览层使用固定透明度，避免多次涂抹同一区域后越来越不透明。
const MASK_COLOR = 'rgba(255, 60, 60, 1)';
const MASK_PREVIEW_OPACITY = 0.8;
const ERASER_CURSOR_COLOR = 'rgba(255, 255, 255, 0.6)';
const BRUSH_CURSOR_COLOR = 'rgba(255, 60, 60, 0.7)';

function proxyImageUrl(url) {
  if (!url) return url;
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  const token = getToken();
  return `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(url)}${token ? '&token=' + encodeURIComponent(token) : ''}`;
}

export default function InpaintMaskEditor({ imageUrl, onClose, onSubmit, allowEmptyMask = false, hint, maskFormat = 'mj' }) {
  // ── State ──
  const [tool, setTool] = useState('brush'); // 'brush' | 'eraser'
  const [brushSize, setBrushSize] = useState(40);
  const [prompt, setPrompt] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // ── Refs ──
  const containerRef = useRef(null);
  const bgCanvasRef = useRef(null);   // 底图层
  const maskCanvasRef = useRef(null); // 遮罩层（用户绘制）
  const cursorCanvasRef = useRef(null); // 光标层
  const imgRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef(null);
  const drawEndTimeRef = useRef(0); // 记录绘制结束时间，防止松键触发关闭
  const canvasSizeRef = useRef({ w: 0, h: 0 });

  // ── 加载底图 ──
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImageLoaded(true);
    };
    img.onerror = () => {
      // 尝试代理
      if (!img.src.includes('/api/proxy-image')) {
        img.src = proxyImageUrl(imageUrl);
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // ── 初始化画布 ──
  useEffect(() => {
    if (!imageLoaded || !imgRef.current) return;
    const img = imgRef.current;

    // 计算画布尺寸（适配屏幕，保持纵横比）
    const maxW = window.innerWidth * 0.85;
    const maxH = window.innerHeight * 0.8;
    const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    canvasSizeRef.current = { w, h, naturalW: img.naturalWidth, naturalH: img.naturalHeight, scale };

    // 设置三个 canvas 的尺寸
    [bgCanvasRef, maskCanvasRef, cursorCanvasRef].forEach(ref => {
      if (ref.current) {
        ref.current.width = w;
        ref.current.height = h;
        ref.current.style.width = `${w}px`;
        ref.current.style.height = `${h}px`;
      }
    });

    // 绘制底图
    const bgCtx = bgCanvasRef.current.getContext('2d');
    bgCtx.drawImage(img, 0, 0, w, h);

    // 初始化历史（空 mask）
    const maskCtx = maskCanvasRef.current.getContext('2d');
    maskCtx.clearRect(0, 0, w, h);
    saveHistory();
  }, [imageLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 保存历史快照 ──
  const saveHistory = useCallback(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(imageData);
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // ── 撤销 / 重做 ──
  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    const ctx = maskCanvasRef.current.getContext('2d');
    ctx.putImageData(history[newIndex], 0, 0);
    setHistoryIndex(newIndex);
  }, [historyIndex, history]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    const ctx = maskCanvasRef.current.getContext('2d');
    ctx.putImageData(history[newIndex], 0, 0);
    setHistoryIndex(newIndex);
  }, [historyIndex, history]);

  // ── 获取鼠标在 canvas 上的坐标 ──
  const getCanvasPos = useCallback((e) => {
    const rect = maskCanvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // ── 绘制一笔 ──
  const drawStroke = useCallback((from, to) => {
    const ctx = maskCanvasRef.current.getContext('2d');
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === 'brush') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = MASK_COLOR;
    } else {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    }

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }, [tool, brushSize]);

  // ── 绘制光标圆圈 ──
  const drawCursor = useCallback((pos) => {
    const canvas = cursorCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!pos) return;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
    ctx.strokeStyle = tool === 'brush' ? BRUSH_CURSOR_COLOR : ERASER_CURSOR_COLOR;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [brushSize, tool]);

  // ── 鼠标/触控事件 ──
  //
  // ⚠️ 重要设计说明（请勿轻易修改）：
  //
  // 这里使用了 **三层防护** 解决「画笔移出画布导致弹窗关闭」的历史 bug：
  //
  //   1. setPointerCapture — pointerDown 时捕获指针，使画布在鼠标移出后
  //      仍能接收 move/up 事件（覆盖 90% 的正常速度操作场景）
  //
  //   2. window pointerup 兜底 — 极快速滑出时浏览器的 setPointerCapture
  //      可能跟不上（已知 Chrome/Safari 行为），window 级别的 pointerup
  //      确保 isDrawingRef 一定能被清除
  //
  //   3. drawEndTimeRef + 300ms 防抖 — 绘制结束后 300ms 内，容器背景
  //      的 onClick(onClose) 被抑制。因为快速松键时，pointerup 可能
  //      冒泡/合成为 click 事件落在容器上，被误判为「点击背景关闭弹窗」
  //
  // 如果要修改绘制事件逻辑，必须同时检查这三层是否仍然协调一致。
  //
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    e.target.setPointerCapture(e.pointerId);  // 防护层 1
    isDrawingRef.current = true;
    const pos = getCanvasPos(e);
    lastPosRef.current = pos;
    drawStroke(pos, pos);
  }, [getCanvasPos, drawStroke]);

  const handlePointerMove = useCallback((e) => {
    const pos = getCanvasPos(e);
    drawCursor(pos);
    if (!isDrawingRef.current) return;
    const last = lastPosRef.current;
    if (last) drawStroke(last, pos);
    lastPosRef.current = pos;
  }, [getCanvasPos, drawStroke, drawCursor]);

  const handlePointerUp = useCallback((e) => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false;
      lastPosRef.current = null;
      drawEndTimeRef.current = Date.now();    // 防护层 3：记录结束时间
      saveHistory();
    }
    if (e.target.hasPointerCapture?.(e.pointerId)) {
      e.target.releasePointerCapture(e.pointerId);
    }
  }, [saveHistory]);

  const handlePointerLeave = useCallback(() => {
    drawCursor(null);
    // 指针已被捕获时不在 leave 中结束绘制 — pointerUp 或 window 兜底会处理
    if (isDrawingRef.current) return;
  }, [drawCursor]);

  // 防护层 2：window 级 pointerup 兜底
  useEffect(() => {
    const handleWindowPointerUp = () => {
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        lastPosRef.current = null;
        drawEndTimeRef.current = Date.now();
        saveHistory();
      }
    };
    window.addEventListener('pointerup', handleWindowPointerUp);
    return () => window.removeEventListener('pointerup', handleWindowPointerUp);
  }, [saveHistory]);

  // ── 键盘快捷键 ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Ctrl+Z / Cmd+Z = undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      // Ctrl+Shift+Z / Cmd+Shift+Z = redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); redo(); }
      // 快捷键 B = 画笔, E = 橡皮擦
      if (e.key === 'b' || e.key === 'B') setTool('brush');
      if (e.key === 'e' || e.key === 'E') setTool('eraser');
      // [ / ] 调整笔刷大小
      if (e.key === '[') setBrushSize(s => Math.max(5, s - 5));
      if (e.key === ']') setBrushSize(s => Math.min(200, s + 5));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, undo, redo]);

  // ── 导出 mask 为 base64 ──
  const exportMaskBase64 = useCallback(() => {
    const maskCanvas = maskCanvasRef.current;
    const { naturalW, naturalH } = canvasSizeRef.current;

    // 先把编辑尺寸的遮罩转成严格黑白，再缩放到原图尺寸。
    // 这样不会因为 alpha 混合导致整图误判为白色。
    const maskCtx = maskCanvas.getContext('2d');
    const srcData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);

    const binaryCanvas = document.createElement('canvas');
    binaryCanvas.width = maskCanvas.width;
    binaryCanvas.height = maskCanvas.height;
    const binaryCtx = binaryCanvas.getContext('2d');
    const binaryImage = binaryCtx.createImageData(maskCanvas.width, maskCanvas.height);
    const srcPixels = srcData.data;
    const binPixels = binaryImage.data;

    if (maskFormat === 'openai') {
      // OpenAI 格式：涂抹区域 → 透明(alpha=0)，未涂抹区域 → 不透明白色(alpha=255)
      for (let i = 0; i < srcPixels.length; i += 4) {
        const isMasked = srcPixels[i + 3] > 20;
        binPixels[i] = 255;
        binPixels[i + 1] = 255;
        binPixels[i + 2] = 255;
        binPixels[i + 3] = isMasked ? 0 : 255;
      }
    } else {
      // MJ 格式：涂抹区域 → 白色，未涂抹区域 → 黑色（全不透明）
      for (let i = 0; i < srcPixels.length; i += 4) {
        const isMasked = srcPixels[i + 3] > 20;
        const v = isMasked ? 255 : 0;
        binPixels[i] = v;
        binPixels[i + 1] = v;
        binPixels[i + 2] = v;
        binPixels[i + 3] = 255;
      }
    }
    binaryCtx.putImageData(binaryImage, 0, 0);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = naturalW;
    outCanvas.height = naturalH;
    const outCtx = outCanvas.getContext('2d');
    if (maskFormat === 'openai') {
      // OpenAI: 不能用 fillRect 填白底，否则 drawImage(source-over) 合成时
      // 透明像素无法覆盖已有的白色背景，导致 mask 全变成不透明白色。
      // 先 clearRect 保持透明，drawImage 后再用二值化清理。
      outCtx.clearRect(0, 0, naturalW, naturalH);
    } else {
      // MJ: 背景黑色（未涂抹=保留=黑色）
      outCtx.fillStyle = '#000000';
      outCtx.fillRect(0, 0, naturalW, naturalH);
    }
    outCtx.imageSmoothingEnabled = false;
    outCtx.drawImage(binaryCanvas, 0, 0, naturalW, naturalH);

    // OpenAI 格式：缩放后再次二值化 alpha，确保没有半透明边缘
    // 同时把未涂抹区域（alpha=255 的像素）确定设为白色不透明
    if (maskFormat === 'openai') {
      const outData = outCtx.getImageData(0, 0, naturalW, naturalH);
      const px = outData.data;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i + 3] < 128) {
          // 透明 = 重绘区域（RGB 也用白色，避免被 API 读取 RGB 显示黑块）
          px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; px[i + 3] = 0;
        } else {
          // 不透明 = 保留区域（白色）
          px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; px[i + 3] = 255;
        }
      }
      outCtx.putImageData(outData, 0, 0);
    }

    // MJ 格式：缩放后必须再二值化一次，确保严格纯黑纯白。
    // ⚠️ 踩坑（2026-08-23）：此前 MJ 分支缺这一步（openai 分支一直都有）。
    //    笔刷是圆头描边 → canvas 抗锯齿；虽然缩放前已按 alpha 二值化，
    //    但紧接着 drawImage 从编辑尺寸(如 1264×842)缩放到原图尺寸(1344×896)
    //    时会重新插值出中间灰。即使 imageSmoothingEnabled=false 也不保险
    //    （各浏览器对该标志的落实程度不一致）。
    //    症状：遮罩 base64 体积虚高约 10 倍（实测 38668 vs 干净二值 3516 字符），
    //    MJ 侧表现为"提交成功(code=1)但完全不按涂抹区工作"。
    if (maskFormat !== 'openai') {
      const outData = outCtx.getImageData(0, 0, naturalW, naturalH);
      const px = outData.data;
      for (let i = 0; i < px.length; i += 4) {
        // 以亮度判定：亮→白(重绘)，暗→黑(保留)；alpha 一律不透明
        const v = px[i] > 127 ? 255 : 0;
        px[i] = v; px[i + 1] = v; px[i + 2] = v; px[i + 3] = 255;
      }
      outCtx.putImageData(outData, 0, 0);
    }

    return outCanvas.toDataURL('image/png');
  }, [maskFormat]);

  // ── 检查 mask 是否为空 ──
  const isMaskEmpty = useCallback(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 20) return false;
    }
    return true;
  }, []);

  // ── 统计遮罩覆盖率（用于后端调试日志） ──
  const getMaskMeta = useCallback(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let masked = 0;
    const total = canvas.width * canvas.height;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 20) masked++;
    }
    const coverage = total > 0 ? Number(((masked / total) * 100).toFixed(2)) : 0;
    return {
      coveragePercent: coverage,
      maskedPixels: masked,
      totalPixels: total,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      naturalWidth: canvasSizeRef.current?.naturalW || canvas.width,
      naturalHeight: canvasSizeRef.current?.naturalH || canvas.height,
    };
  }, []);

  // ── 提交 ──
  const handleSubmit = useCallback(async () => {
    if (!allowEmptyMask && isMaskEmpty()) return;
    setSubmitting(true);
    try {
      const empty = isMaskEmpty();
      const maskBase64 = empty ? undefined : exportMaskBase64();
      const maskMeta = empty ? undefined : getMaskMeta();
      
      // 👁️ 诊断 mask 格式（前端验证纯黑白）— 仅在有遮罩时运行
      if (maskBase64) {
        const diagnosticCanvas = document.createElement('canvas');
        const { naturalW, naturalH } = canvasSizeRef.current;
        diagnosticCanvas.width = naturalW;
        diagnosticCanvas.height = naturalH;
        const diagCtx = diagnosticCanvas.getContext('2d');
        const diagImg = new Image();
        diagImg.onload = () => {
          diagCtx.drawImage(diagImg, 0, 0);
          const data = diagCtx.getImageData(0, 0, naturalW, naturalH).data;
          let whitePixels = 0, blackPixels = 0, otherPixels = 0;
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
            if (a < 128) continue;
            if (r === 255 && g === 255 && b === 255) whitePixels++;
            else if (r === 0 && g === 0 && b === 0) blackPixels++;
            else otherPixels++;
          }
          const total = whitePixels + blackPixels + otherPixels;
          const isClean = otherPixels === 0 || (otherPixels / total < 0.01);
        };
        diagImg.src = maskBase64;
      }

      await onSubmit({ maskBase64, prompt: prompt.trim() || undefined, maskMeta });
    } catch (err) {
      console.error('[InpaintMaskEditor] Submit error:', err);
    } finally {
      setSubmitting(false);
      onClose();
    }
  }, [allowEmptyMask, isMaskEmpty, exportMaskBase64, getMaskMeta, prompt, onSubmit, onClose]);

  // ── 清空遮罩 ──
  const clearMask = useCallback(() => {
    const ctx = maskCanvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
    saveHistory();
  }, [saveHistory]);

  // ── 渲染 ──
  return createPortal(
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={(e) => {
        if (e.target === containerRef.current) {
          // 🔧 绘制刚结束 300ms 内忽略点击，防止画布外松键误触关闭
          if (Date.now() - drawEndTimeRef.current < 300) return;
          onClose();
        }
      }}
    >
      {/* ── Loading ── */}
      {!imageLoaded && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Loader2 size={24} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>加载图片中...</span>
        </div>
      )}

      {imageLoaded && (
        <>
          {/* ── 顶部工具栏 ── */}
          <div style={styles.toolbar}>
            {/* 画笔 */}
            <ToolBtn
              active={tool === 'brush'}
              onClick={() => setTool('brush')}
              title="画笔 (B)"
            >
              <Brush size={16} /> <span>画笔</span>
            </ToolBtn>

            {/* 橡皮擦 */}
            <ToolBtn
              active={tool === 'eraser'}
              onClick={() => setTool('eraser')}
              title="橡皮擦 (E)"
            >
              <Eraser size={16} /> <span>橡皮擦</span>
            </ToolBtn>

            <div style={styles.divider} />

            {/* 笔头大小 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>笔头</span>
              <input
                type="range"
                min={5}
                max={200}
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                style={{ width: 100, accentColor: '#3b82f6' }}
              />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', minWidth: 28 }}>{brushSize}px</span>
            </div>

            <div style={styles.divider} />

            {/* 撤销 / 重做 */}
            <ToolBtn onClick={undo} disabled={historyIndex <= 0} title="撤销 (Ctrl+Z)">
              <Undo2 size={16} />
            </ToolBtn>
            <ToolBtn onClick={redo} disabled={historyIndex >= history.length - 1} title="重做 (Ctrl+Shift+Z)">
              <Redo2 size={16} />
            </ToolBtn>

            {/* 清空 */}
            <ToolBtn onClick={clearMask} title="清空遮罩">
              <span style={{ fontSize: 11 }}>清空</span>
            </ToolBtn>

            <div style={{ flex: 1 }} />

            {/* 提示词输入 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>提示词</span>
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="可选：描述重绘内容"
                style={styles.promptInput}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); e.stopPropagation(); }}
              />
            </div>

            <div style={styles.divider} />

            {/* 取消 / 确认 */}
            <ToolBtn onClick={onClose} title="取消 (Esc)">
              <X size={16} /> <span>取消</span>
            </ToolBtn>
            <ToolBtn
              onClick={handleSubmit}
              disabled={submitting}
              accent
              title="提交局部重绘"
            >
              {submitting ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={16} />}
              <span>提交</span>
            </ToolBtn>
          </div>

          {/* ── 画布区域 ── */}
          <div style={{ position: 'relative', cursor: 'none' }}>
            {/* 底图 */}
            <canvas ref={bgCanvasRef} style={{ position: 'absolute', top: 0, left: 0, borderRadius: 4 }} />
            {/* 遮罩层 */}
            <canvas
              ref={maskCanvasRef}
              style={{ position: 'absolute', top: 0, left: 0, borderRadius: 4, opacity: MASK_PREVIEW_OPACITY }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerLeave}
            />
            {/* 光标层 */}
            <canvas
              ref={cursorCanvasRef}
              style={{ position: 'relative', top: 0, left: 0, borderRadius: 4, pointerEvents: 'none' }}
            />
          </div>

          {/* ── 底部提示 ── */}
          <div style={{ marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            {hint || '用画笔涂抹要重新生成的区域 · 白色区域将被 MJ 重新绘制 · 可输入提示词引导重绘内容'}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

// ── 工具按钮组件 ──
function ToolBtn({ children, onClick, disabled, active, accent, title }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 10px', borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        background: accent
          ? (hover ? '#2563eb' : '#3b82f6')
          : active
            ? 'rgba(255,255,255,0.18)'
            : hover ? 'rgba(255,255,255,0.1)' : 'transparent',
        color: accent ? '#fff' : active ? '#fff' : 'rgba(255,255,255,0.85)',
        fontSize: 12, fontWeight: 500,
        transition: 'background 0.15s',
        whiteSpace: 'nowrap',
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}

// ── 样式 ──
const styles = {
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'rgba(30, 30, 30, 0.9)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '6px 12px',
    marginBottom: 12,
    backdropFilter: 'blur(16px)',
    flexWrap: 'wrap',
    maxWidth: '90vw',
  },
  divider: {
    width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 4px',
  },
  promptInput: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6,
    padding: '4px 8px',
    color: '#fff',
    fontSize: 12,
    width: 200,
    outline: 'none',
  },
};
